import { useState, useEffect, Fragment } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  CircularProgress,
  Alert,
  Slider,
  FormHelperText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Checkbox,
  FormGroup,
  FormControlLabel,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  Search as SearchIcon,
  Summarize as SummaryIcon,
  Quiz as FAQIcon,
  ExpandMore as ExpandIcon,
  Description as DocumentIcon,
  Tag as TagIcon,
} from '@mui/icons-material';
import { RagTagService } from '../services/ragTagService';
import { RagTagSession, RagTagContent } from '../models/ragTag';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && (
        <Box sx={{ py: { xs: 2, sm: 3 } }}>
          {children}
        </Box>
      )}
    </div>
  );
};

export const RagTagSystem: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [activeTab, setActiveTab] = useState(0);
  const [sessions, setSessions] = useState<RagTagSession[]>([]);
  const [currentSession, setCurrentSession] = useState<RagTagSession | null>(null);
  const [uploadContent, setUploadContent] = useState('');
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadTags, setUploadTags] = useState('');
  const [uploadType, setUploadType] = useState<'text' | 'markdown'>('text');
  const [query, setQuery] = useState('');
  const [selectedContents, setSelectedContents] = useState<string[]>([]);
  const [summaryType, setSummaryType] = useState<'brief' | 'detailed' | 'executive'>('brief');
  const [faqCount, setFaqCount] = useState(5);
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(1024);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = () => {
    const allSessions = RagTagService.getAllSessions();
    setSessions(allSessions);
  };

  const handleNewSession = () => {
    const session = RagTagService.createSession();
    setCurrentSession(session);
    loadSessions();
    setSelectedContents([]);
  };

  const handleUpload = async () => {
    if (!currentSession) {
      setError('Please start a session first');
      return;
    }

    if (!uploadContent.trim()) {
      setError('Please enter content to upload');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await RagTagService.upload({
        content: uploadContent,
        contentType: uploadType,
        metadata: {
          title: uploadTitle || undefined,
          tags: uploadTags ? uploadTags.split(',').map(t => t.trim()) : undefined,
        },
      });

      // Get full content details
      const fullContent = await RagTagService.getContent(response.contentId);
      RagTagService.addContent(currentSession.id, fullContent);
      setCurrentSession(RagTagService.getSession(currentSession.id));
      
      // Clear form
      setUploadContent('');
      setUploadTitle('');
      setUploadTags('');
      setUploadDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload content');
    } finally {
      setLoading(false);
    }
  };

  const handleQuery = async () => {
    if (!currentSession) {
      setError('Please start a session first');
      return;
    }

    if (!query.trim()) {
      setError('Please enter a query');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await RagTagService.query({
        query,
        contentIds: selectedContents.length > 0 ? selectedContents : undefined,
        temperature,
        maxTokens,
      });

      RagTagService.addQuery(currentSession.id, query, response);
      setCurrentSession(RagTagService.getSession(currentSession.id));
      setQuery('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to process query');
    } finally {
      setLoading(false);
    }
  };

  const handleSummarize = async () => {
    if (!currentSession) {
      setError('Please start a session first');
      return;
    }

    if (selectedContents.length === 0) {
      setError('Please select at least one document to summarize');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await RagTagService.summarize({
        contentIds: selectedContents,
        summaryType,
        temperature,
        maxTokens,
      });

      RagTagService.addSummary(currentSession.id, selectedContents, summaryType, response.summary);
      setCurrentSession(RagTagService.getSession(currentSession.id));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate summary');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateFAQ = async () => {
    if (!currentSession) {
      setError('Please start a session first');
      return;
    }

    if (selectedContents.length === 0) {
      setError('Please select at least one document for FAQ generation');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await RagTagService.generateFAQ({
        contentIds: selectedContents,
        numberOfQuestions: faqCount,
        temperature,
        maxTokens,
      });

      RagTagService.addFAQs(currentSession.id, selectedContents, response);
      setCurrentSession(RagTagService.getSession(currentSession.id));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate FAQs');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadSession = (session: RagTagSession) => {
    setCurrentSession(session);
    setSelectedContents([]);
    setHistoryOpen(false);
  };

  const handleDeleteSession = (sessionId: string) => {
    RagTagService.deleteSession(sessionId);
    if (currentSession?.id === sessionId) {
      setCurrentSession(null);
      setSelectedContents([]);
    }
    loadSessions();
  };

  const handleDeleteContent = (contentId: string) => {
    if (!currentSession) return;
    RagTagService.removeContent(currentSession.id, contentId);
    setCurrentSession(RagTagService.getSession(currentSession.id));
    setSelectedContents(selectedContents.filter(id => id !== contentId));
  };

  const handleExport = () => {
    if (!currentSession) return;

    const content = RagTagService.exportSession(currentSession);
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ragtag-knowledge-base-${currentSession.id}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleContentSelection = (contentId: string) => {
    setSelectedContents(prev => {
      if (prev.includes(contentId)) {
        return prev.filter(id => id !== contentId);
      } else {
        return [...prev, contentId];
      }
    });
  };

  const handleSelectAll = () => {
    if (!currentSession) return;
    if (selectedContents.length === currentSession.contents.length) {
      setSelectedContents([]);
    } else {
      setSelectedContents(currentSession.contents.map(c => c.contentId));
    }
  };

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          mb: { xs: 2, sm: 3 },
          gap: { xs: 2, sm: 0 }
        }}>
          <Typography 
            variant="h4" 
            component="h1"
            sx={{ 
              fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' }
            }}
          >
            {isMobile ? 'RAG Knowledge' : 'RAG Tag Knowledge Base'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="contained"
              startIcon={!isMobile && <UploadIcon />}
              onClick={handleNewSession}
              sx={{ 
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                minHeight: 44,
                flex: { xs: 1, sm: 'initial' }
              }}
            >
              {isMobile ? 'New' : 'New Session'}
            </Button>
            <IconButton 
              onClick={() => setHistoryOpen(true)} 
              title={isMobile ? "" : "Session History"}
              sx={{ minHeight: 44, minWidth: 44 }}
            >
              <HistoryIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
            </IconButton>
            {currentSession && (
              <IconButton 
                onClick={handleExport} 
                title={isMobile ? "" : "Export Session"}
                sx={{ minHeight: 44, minWidth: 44 }}
              >
                <DownloadIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
              </IconButton>
            )}
          </Box>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: { xs: 2, sm: 2 } }} 
            onClose={() => setError(null)}
          >
            <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              {error}
            </Typography>
          </Alert>
        )}

        {!currentSession ? (
          <Paper sx={{ p: { xs: 3, sm: 4, md: 6 }, textAlign: 'center' }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
            >
              {isMobile ? 'RAG Knowledge Base' : 'Welcome to RAG Tag Knowledge Base'}
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                mb: { xs: 2, sm: 3 },
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              {isMobile 
                ? 'Upload documents and query with AI' 
                : 'Upload documents and query them using AI-powered search and analysis.'}
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={!isMobile && <UploadIcon />}
              onClick={handleNewSession}
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minHeight: 48
              }}
            >
              {isMobile ? 'Start Session' : 'Start New Session'}
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between', 
                  alignItems: { xs: 'stretch', sm: 'center' }, 
                  mb: { xs: 1.5, sm: 2 },
                  gap: { xs: 1.5, sm: 0 }
                }}>
                  <Typography 
                    variant="h6"
                    sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
                  >
                    {isMobile ? 'Documents' : 'Knowledge Base'}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={!isMobile && <UploadIcon />}
                    onClick={() => setUploadDialogOpen(true)}
                    sx={{ 
                      fontSize: { xs: '0.75rem', sm: '0.8rem' },
                      minHeight: 36
                    }}
                  >
                    {isMobile ? 'Add' : 'Add Document'}
                  </Button>
                </Box>

                {currentSession.contents.length === 0 ? (
                  <Alert 
                    severity="info"
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    {isMobile 
                      ? 'No documents yet. Click "Add" to start.' 
                      : 'No documents uploaded yet. Click "Add Document" to start.'}
                  </Alert>
                ) : (
                  <>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedContents.length === currentSession.contents.length}
                          indeterminate={selectedContents.length > 0 && selectedContents.length < currentSession.contents.length}
                          onChange={handleSelectAll}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Select All
                        </Typography>
                      }
                      sx={{ mb: { xs: 1, sm: 2 } }}
                    />
                    <List>
                      {currentSession.contents.map((content) => (
                        <Fragment key={content.contentId}>
                          <ListItem>
                            <Checkbox
                              checked={selectedContents.includes(content.contentId)}
                              onChange={() => handleContentSelection(content.contentId)}
                            />
                            <ListItemText
                              primary={
                                <Typography sx={{ fontSize: { xs: '0.875rem', sm: '0.95rem' } }}>
                                  {isMobile && content.metadata?.title 
                                    ? content.metadata.title.length > 25 
                                      ? `${content.metadata.title.substring(0, 25)}...` 
                                      : content.metadata.title
                                    : content.metadata?.title || content.contentId}
                                </Typography>
                              }
                              secondary={
                                <>
                                  <Typography 
                                    variant="caption" 
                                    display="block"
                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                  >
                                    {isMobile 
                                      ? `${content.contentType} | ${content.metadata?.chunks || 'N/A'} chunks`
                                      : `Type: ${content.contentType} | Chunks: ${content.metadata?.chunks || 'N/A'}`}
                                  </Typography>
                                  {content.metadata?.tags && (
                                    <Box sx={{ mt: 0.5 }}>
                                      {content.metadata.tags.slice(0, isMobile ? 2 : 5).map((tag, i) => (
                                        <Chip
                                          key={i}
                                          label={tag}
                                          size="small"
                                          sx={{ 
                                            mr: 0.5, 
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                          }}
                                        />
                                      ))}
                                      {isMobile && content.metadata.tags.length > 2 && (
                                        <Chip
                                          label={`+${content.metadata.tags.length - 2}`}
                                          size="small"
                                          sx={{ fontSize: '0.7rem' }}
                                        />
                                      )}
                                    </Box>
                                  )}
                                </>
                              }
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                onClick={() => handleDeleteContent(content.contentId)}
                                title={isMobile ? "" : "Delete document"}
                                sx={{ minHeight: 44, minWidth: 44 }}
                              >
                                <DeleteIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                          <Divider />
                        </Fragment>
                      ))}
                    </List>
                  </>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={8}>
              <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Tabs 
                  value={activeTab} 
                  onChange={(_, val) => setActiveTab(val)} 
                  sx={{ 
                    mb: { xs: 2, sm: 2 },
                    minHeight: 48,
                    '& .MuiTab-root': {
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      minHeight: 48,
                    },
                    ...(isTablet && {
                      '& .MuiTab-iconWrapper': {
                        display: 'none',
                      },
                    }),
                  }}
                  variant={isMobile ? "fullWidth" : "standard"}
                >
                  <Tab 
                    label={isMobile ? "Query" : "Query"} 
                    icon={<SearchIcon />} 
                    iconPosition="start" 
                  />
                  <Tab 
                    label={isMobile ? "Summary" : "Summarize"} 
                    icon={<SummaryIcon />} 
                    iconPosition="start" 
                  />
                  <Tab 
                    label={isMobile ? "FAQ" : "Generate FAQ"} 
                    icon={<FAQIcon />} 
                    iconPosition="start" 
                  />
                  <Tab 
                    label={isMobile ? "Settings" : "Settings"} 
                    icon={<TagIcon />} 
                    iconPosition="start" 
                  />
                </Tabs>

                <TabPanel value={activeTab} index={0}>
                  <Box>
                    <TextField
                      fullWidth
                      label={isMobile ? "Ask a question" : "Ask a question about your documents"}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder={isMobile ? "What are the key findings?" : "e.g., What are the key findings? How does X relate to Y?"}
                      sx={{ mb: { xs: 1.5, sm: 2 } }}
                      InputProps={{
                        sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                      }}
                      InputLabelProps={{
                        sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                      }}
                    />
                    
                    <Alert 
                      severity="info" 
                      sx={{ 
                        mb: { xs: 1.5, sm: 2 },
                        '& .MuiAlert-message': {
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }
                      }}
                    >
                      {selectedContents.length === 0
                        ? (isMobile ? 'Searching all docs' : 'Searching across all documents')
                        : `Searching in ${selectedContents.length} ${isMobile ? 'doc(s)' : 'selected document(s)'}`}
                    </Alert>
                    
                    <Button
                      variant="contained"
                      onClick={handleQuery}
                      disabled={!query.trim() || loading}
                      startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <SearchIcon />)}
                      sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        minHeight: 44
                      }}
                    >
                      {loading ? 'Searching...' : 'Search'}
                    </Button>

                    {currentSession.queries.length > 0 && (
                      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                        <Typography 
                          variant="h6" 
                          gutterBottom
                          sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                        >
                          Query History
                        </Typography>
                        {currentSession.queries.map((q, index) => (
                          <Accordion key={index}>
                            <AccordionSummary expandIcon={<ExpandIcon />}>
                              <Typography sx={{ fontSize: { xs: '0.875rem', sm: '0.95rem' } }}>
                                {isMobile && q.query.length > 50 
                                  ? `${q.query.substring(0, 50)}...` 
                                  : q.query}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Typography 
                                sx={{ 
                                  mb: { xs: 1.5, sm: 2 },
                                  fontSize: { xs: '0.875rem', sm: '1rem' }
                                }}
                              >
                                {q.answer}
                              </Typography>
                              {q.relevantChunks.length > 0 && (
                                <Box>
                                  <Typography 
                                    variant="subtitle2" 
                                    gutterBottom
                                    sx={{ fontSize: { xs: '0.875rem', sm: '0.95rem' } }}
                                  >
                                    Sources:
                                  </Typography>
                                  {q.relevantChunks.map((chunk, i) => (
                                    <Typography 
                                      key={i} 
                                      variant="caption" 
                                      display="block"
                                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                    >
                                      • {isMobile && chunk.metadata?.title 
                                          ? chunk.metadata.title.length > 20 
                                            ? `${chunk.metadata.title.substring(0, 20)}...` 
                                            : chunk.metadata.title
                                          : chunk.metadata?.title || chunk.contentId} (Score: {chunk.score.toFixed(3)})
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                              <Typography 
                                variant="caption" 
                                color="text.secondary" 
                                sx={{ 
                                  mt: 1, 
                                  display: 'block',
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                }}
                              >
                                {isMobile 
                                  ? new Date(q.timestamp).toLocaleDateString()
                                  : new Date(q.timestamp).toLocaleString()}
                              </Typography>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Box>
                    )}
                  </Box>
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                  <Box>
                    {selectedContents.length === 0 ? (
                      <Alert 
                        severity="warning" 
                        sx={{ 
                          mb: { xs: 1.5, sm: 2 },
                          '& .MuiAlert-message': {
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                          }
                        }}
                      >
                        {isMobile 
                          ? 'Select at least 1 document' 
                          : 'Please select at least one document to summarize'}
                      </Alert>
                    ) : (
                      <Alert 
                        severity="info" 
                        sx={{ 
                          mb: { xs: 1.5, sm: 2 },
                          '& .MuiAlert-message': {
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                          }
                        }}
                      >
                        Summarizing {selectedContents.length} document(s)
                      </Alert>
                    )}

                    <FormControl fullWidth sx={{ mb: { xs: 1.5, sm: 2 } }}>
                      <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        Summary Type
                      </InputLabel>
                      <Select
                        value={summaryType}
                        onChange={(e) => setSummaryType(e.target.value as any)}
                        label="Summary Type"
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        <MenuItem value="brief" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Brief Summary
                        </MenuItem>
                        <MenuItem value="detailed" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Detailed Summary
                        </MenuItem>
                        <MenuItem value="executive" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Executive Summary
                        </MenuItem>
                      </Select>
                    </FormControl>

                    <Button
                      variant="contained"
                      onClick={handleSummarize}
                      disabled={selectedContents.length === 0 || loading}
                      startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <SummaryIcon />)}
                      sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        minHeight: 44
                      }}
                    >
                      {loading ? 'Generating...' : (isMobile ? 'Generate' : 'Generate Summary')}
                    </Button>

                    {currentSession.summaries.length > 0 && (
                      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                        <Typography 
                          variant="h6" 
                          gutterBottom
                          sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                        >
                          Summary History
                        </Typography>
                        {currentSession.summaries.map((s, index) => (
                          <Card key={index} sx={{ mb: { xs: 1.5, sm: 2 } }}>
                            <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                              <Typography 
                                variant="subtitle2" 
                                color="primary" 
                                gutterBottom
                                sx={{ fontSize: { xs: '0.875rem', sm: '0.95rem' } }}
                              >
                                {s.type.replace('_', ' ').toUpperCase()} - {s.contentIds.length} {isMobile ? 'doc(s)' : 'document(s)'}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  whiteSpace: 'pre-wrap',
                                  fontSize: { xs: '0.875rem', sm: '1rem' }
                                }}
                              >
                                {s.summary}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                color="text.secondary" 
                                sx={{ 
                                  mt: 1, 
                                  display: 'block',
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                }}
                              >
                                {isMobile 
                                  ? new Date(s.timestamp).toLocaleDateString()
                                  : new Date(s.timestamp).toLocaleString()}
                              </Typography>
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    )}
                  </Box>
                </TabPanel>

                <TabPanel value={activeTab} index={2}>
                  <Box>
                    {selectedContents.length === 0 ? (
                      <Alert 
                        severity="warning" 
                        sx={{ 
                          mb: { xs: 1.5, sm: 2 },
                          '& .MuiAlert-message': {
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                          }
                        }}
                      >
                        {isMobile 
                          ? 'Select at least 1 document' 
                          : 'Please select at least one document for FAQ generation'}
                      </Alert>
                    ) : (
                      <Alert 
                        severity="info" 
                        sx={{ 
                          mb: { xs: 1.5, sm: 2 },
                          '& .MuiAlert-message': {
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                          }
                        }}
                      >
                        {isMobile 
                          ? `FAQs from ${selectedContents.length} doc(s)`
                          : `Generating FAQs from ${selectedContents.length} document(s)`}
                      </Alert>
                    )}

                    <FormControl fullWidth sx={{ mb: { xs: 1.5, sm: 2 } }}>
                      <Typography 
                        gutterBottom
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        {isMobile ? `Questions: ${faqCount}` : `Number of Questions: ${faqCount}`}
                      </Typography>
                      <Slider
                        value={faqCount}
                        onChange={(_, val) => setFaqCount(val as number)}
                        min={3}
                        max={20}
                        marks
                        valueLabelDisplay="auto"
                      />
                      <FormHelperText sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                        {isMobile ? 'FAQ questions to generate' : 'How many FAQ questions to generate'}
                      </FormHelperText>
                    </FormControl>

                    <Button
                      variant="contained"
                      onClick={handleGenerateFAQ}
                      disabled={selectedContents.length === 0 || loading}
                      startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <FAQIcon />)}
                      sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        minHeight: 44
                      }}
                    >
                      {loading ? 'Generating...' : (isMobile ? 'Generate' : 'Generate FAQs')}
                    </Button>

                    {currentSession.faqs.length > 0 && (
                      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                        <Typography 
                          variant="h6" 
                          gutterBottom
                          sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                        >
                          Generated FAQs
                        </Typography>
                        {currentSession.faqs.map((faqSet, setIndex) => (
                          <Card key={setIndex} sx={{ mb: { xs: 1.5, sm: 2 } }}>
                            <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                              <Typography 
                                variant="subtitle2" 
                                color="text.secondary" 
                                gutterBottom
                                sx={{ fontSize: { xs: '0.875rem', sm: '0.95rem' } }}
                              >
                                From {faqSet.contentIds.length} document(s)
                              </Typography>
                              {faqSet.faqs.map((faq, index) => (
                                <Box key={index} sx={{ mb: { xs: 1.5, sm: 2 } }}>
                                  <Typography 
                                    variant="subtitle2" 
                                    color="primary"
                                    sx={{ fontSize: { xs: '0.875rem', sm: '0.95rem' } }}
                                  >
                                    Q{index + 1}: {faq.question}
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      mt: 1,
                                      fontSize: { xs: '0.875rem', sm: '1rem' }
                                    }}
                                  >
                                    {faq.answer}
                                  </Typography>
                                  {index < faqSet.faqs.length - 1 && <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />}
                                </Box>
                              ))}
                              <Typography 
                                variant="caption" 
                                color="text.secondary" 
                                sx={{ 
                                  mt: 1, 
                                  display: 'block',
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                }}
                              >
                                Generated: {isMobile 
                                  ? new Date(faqSet.timestamp).toLocaleDateString()
                                  : new Date(faqSet.timestamp).toLocaleString()}
                              </Typography>
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    )}
                  </Box>
                </TabPanel>

                <TabPanel value={activeTab} index={3}>
                  <Box>
                    <Typography 
                      variant="h6" 
                      gutterBottom
                      sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                    >
                      Model Settings
                    </Typography>
                    
                    <FormControl fullWidth sx={{ mb: { xs: 2, sm: 3 } }}>
                      <Typography 
                        gutterBottom
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        Temperature: {temperature}
                      </Typography>
                      <Slider
                        value={temperature}
                        onChange={(_, val) => setTemperature(val as number)}
                        min={0}
                        max={1}
                        step={0.1}
                        marks
                        valueLabelDisplay="auto"
                      />
                      <FormHelperText sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                        {isMobile ? '0 = focused, 1 = creative' : 'Controls randomness: 0 = focused, 1 = creative'}
                      </FormHelperText>
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: { xs: 2, sm: 3 } }}>
                      <Typography 
                        gutterBottom
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        Max Tokens: {maxTokens}
                      </Typography>
                      <Slider
                        value={maxTokens}
                        onChange={(_, val) => setMaxTokens(val as number)}
                        min={256}
                        max={4096}
                        step={256}
                        marks
                        valueLabelDisplay="auto"
                      />
                      <FormHelperText sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                        {isMobile ? 'Max response length' : 'Maximum length of the response'}
                      </FormHelperText>
                    </FormControl>

                    <Alert 
                      severity="info"
                      sx={{
                        '& .MuiAlert-message': {
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }
                      }}
                    >
                      {isMobile 
                        ? 'Settings apply to all operations' 
                        : 'These settings apply to all operations (query, summarize, and FAQ generation).'}
                    </Alert>
                  </Box>
                </TabPanel>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Session History Dialog */}
        <Dialog 
          open={historyOpen} 
          onClose={() => setHistoryOpen(false)} 
          maxWidth="md" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
            Session History
          </DialogTitle>
          <DialogContent>
            <List>
              {sessions.map((session) => (
                <Fragment key={session.id}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Typography sx={{ fontSize: { xs: '0.875rem', sm: '0.95rem' } }}>
                          {isMobile && session.id.length > 25 
                            ? `${session.id.substring(0, 25)}...` 
                            : session.id}
                        </Typography>
                      }
                      secondary={
                        <Typography 
                          variant="caption"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        >
                          {isMobile 
                            ? `${new Date(session.createdAt).toLocaleDateString()} | ${session.contents.length} docs`
                            : `Created: ${new Date(session.createdAt).toLocaleString()} | 
                              ${session.contents.length} documents, 
                              ${session.queries.length} queries, 
                              ${session.summaries.length} summaries, 
                              ${session.faqs.length} FAQ sets`}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        onClick={() => handleLoadSession(session)}
                        title={isMobile ? "" : "Load session"}
                        sx={{ minHeight: 44, minWidth: 44 }}
                      >
                        <SaveIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleDeleteSession(session.id)}
                        title={isMobile ? "" : "Delete session"}
                        sx={{ minHeight: 44, minWidth: 44 }}
                      >
                        <DeleteIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </Fragment>
              ))}
            </List>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, sm: 2 } }}>
            <Button 
              onClick={() => setHistoryOpen(false)}
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minHeight: 44
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Upload Dialog */}
        <Dialog 
          open={uploadDialogOpen} 
          onClose={() => setUploadDialogOpen(false)} 
          maxWidth="md" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
            Upload Document
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: { xs: 1, sm: 2 } }}>
              <TextField
                fullWidth
                label="Document Title"
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                sx={{ mb: { xs: 1.5, sm: 2 } }}
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />

              <TextField
                fullWidth
                label="Tags (comma-separated)"
                value={uploadTags}
                onChange={(e) => setUploadTags(e.target.value)}
                placeholder={isMobile ? "e.g., technical, api" : "e.g., technical, api, documentation"}
                sx={{ mb: { xs: 1.5, sm: 2 } }}
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />

              <FormControl fullWidth sx={{ mb: { xs: 1.5, sm: 2 } }}>
                <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Content Type
                </InputLabel>
                <Select
                  value={uploadType}
                  onChange={(e) => setUploadType(e.target.value as any)}
                  label="Content Type"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  <MenuItem value="text" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Plain Text
                  </MenuItem>
                  <MenuItem value="markdown" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Markdown
                  </MenuItem>
                </Select>
              </FormControl>

              <TextField
                multiline
                rows={isMobile ? 8 : 10}
                fullWidth
                label="Document Content"
                value={uploadContent}
                onChange={(e) => setUploadContent(e.target.value)}
                placeholder="Paste your document content here..."
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, sm: 2 } }}>
            <Button 
              onClick={() => setUploadDialogOpen(false)}
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minHeight: 36
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUpload} 
              disabled={!uploadContent.trim() || loading}
              startIcon={loading && <CircularProgress size={20} />}
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minHeight: 44
              }}
            >
              {loading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};